/**
 * Background Analysis Utility
 * Analyzes image brightness to recommend text/logo colors
 */

/**
 * @typedef {Object} BackgroundAnalysis
 * @property {number} brightness - Average brightness (0-255)
 * @property {boolean} isDark - Whether background is considered dark
 * @property {string} recommendedTextColor - Hex color for text
 * @property {'white' | 'black' | 'color'} recommendedLogoMode - Logo color mode
 */

/**
 * Analyzes background image brightness and returns color recommendations
 * @param {string} imageUrl - URL of the background image
 * @returns {Promise<BackgroundAnalysis>}
 */
export async function analyzeBackground(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas size to match image
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Sample grid points (avoid edges which might have artifacts)
        const samplePoints = [];
        const gridSize = 5;

        for (let x = 0; x < gridSize; x++) {
          for (let y = 0; y < gridSize; y++) {
            const xPos = ((x + 1) / (gridSize + 1)) * canvas.width;
            const yPos = ((y + 1) / (gridSize + 1)) * canvas.height;
            samplePoints.push({ x: Math.floor(xPos), y: Math.floor(yPos) });
          }
        }

        // Calculate average brightness
        let totalBrightness = 0;
        samplePoints.forEach((point) => {
          const pixel = ctx.getImageData(point.x, point.y, 1, 1).data;
          // Calculate perceived brightness using luminance formula
          const brightness =
            0.299 * pixel[0] + 0.587 * pixel[1] + 0.114 * pixel[2];
          totalBrightness += brightness;
        });

        const avgBrightness = totalBrightness / samplePoints.length;
        const isDark = avgBrightness < 128;

        resolve({
          brightness: avgBrightness,
          isDark,
          recommendedTextColor: isDark ? "#FFFFFF" : "#000000",
          recommendedLogoMode: isDark
            ? "white"
            : avgBrightness < 180
              ? "black"
              : "color"
        });
      } catch (error) {
        console.error("Error analyzing background:", error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error("Failed to load image for analysis:", error);
      reject(new Error("Failed to load image for background analysis"));
    };

    img.src = imageUrl;
  });
}

/**
 * Get color contrast ratio between two colors
 * @param {string} color1 - Hex color
 * @param {string} color2 - Hex color
 * @returns {number} Contrast ratio (1-21)
 */
export function getContrastRatio(color1, color2) {
  const getLuminance = (hexColor) => {
    const rgb = hexColor
      .replace("#", "")
      .match(/.{2}/g)
      .map((x) => parseInt(x, 16) / 255);

    const [r, g, b] = rgb.map((val) => {
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Check if text color has sufficient contrast with background
 * @param {string} textColor - Hex color
 * @param {number} bgBrightness - Background brightness (0-255)
 * @returns {boolean}
 */
export function hasGoodContrast(textColor, bgBrightness) {
  const bgColor =
    bgBrightness > 128 ? "#FFFFFF" : bgBrightness > 64 ? "#808080" : "#000000";

  const ratio = getContrastRatio(textColor, bgColor);
  return ratio >= 4.5; // WCAG AA standard for normal text
}
